import React from 'react';
import { sanitizeSlugInput, slugify } from '../attributesMeta';
import { useResetOnKeyChange } from '@/utils';

export type AttributeValueFormMode = 'create' | 'edit';

export type AttributeValueFormValues = {
    name: string;
    slug: string;
};

type UseAttributeValueFormParams = {
    initialValues?: AttributeValueFormValues;
    isOpen: boolean;
    mode?: AttributeValueFormMode;
    onSubmit: (input: AttributeValueFormValues) => void;
};

const EMPTY_INITIAL: AttributeValueFormValues = { name: '', slug: '' };

/**
 * Form state for the Add / Edit value modal (name + slug; `position` is owned
 * by the values sheet). Same slug-derivation + re-seed-on-transition behaviour
 * as the type form.
 */
export const useAttributeValueForm = ({
    initialValues,
    isOpen,
    mode = 'create',
    onSubmit,
}: UseAttributeValueFormParams) => {
    const initial = initialValues ?? EMPTY_INITIAL;

    const [name, setName] = React.useState(initial.name);
    const [slug, setSlug] = React.useState(initial.slug);
    const [isSlugEdited, setIsSlugEdited] = React.useState(mode === 'edit');

    // Re-seed on open/close transitions only — see useCategoryForm.
    useResetOnKeyChange(isOpen, () => {
        setName(initial.name);
        setSlug(initial.slug);
        setIsSlugEdited(mode === 'edit');
    });

    const onNameChange = React.useCallback(
        (value: string) => {
            setName(value);
            if (!isSlugEdited) {
                setSlug(slugify(value));
            }
        },
        [isSlugEdited],
    );

    const onSlugChange = React.useCallback((value: string) => {
        setIsSlugEdited(true);
        setSlug(sanitizeSlugInput(value));
    }, []);

    const trimmedName = name.trim();
    const isValid = trimmedName.length > 0 && slug.length > 0;

    const onSubmitClick = React.useCallback(() => {
        if (!isValid) {
            return;
        }
        onSubmit({ name: trimmedName, slug });
    }, [isValid, onSubmit, slug, trimmedName]);

    return {
        name,
        slug,
        isValid,
        onNameChange,
        onSlugChange,
        onSubmit: onSubmitClick,
    };
};
